import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Scenario } from '../../types';
import { GameplayScreen } from '../gameplay-screen';
import { GameProvider } from '../../systems/game-provider';
import { ScenarioLoader } from '../../systems/scenario-loader';
import type { ScenarioAssetLayer } from '../../systems/scenario-asset-url';
import { useTextPlaceholders } from './TextPlaceholderContext';
import styles from './scenario-studio.module.css';

interface PreviewModalProps {
  scenario: Scenario;
  scenarioId: string;
  startSceneId?: string;
  assetLayer?: ScenarioAssetLayer;
  onClose: () => void;
}

export const PreviewModal: React.FC<PreviewModalProps> = ({
  scenario,
  scenarioId,
  startSceneId,
  assetLayer = 'master',
  onClose,
}) => {
  const placeholderContext = useTextPlaceholders();
  const previewScenario = useMemo(() => {
    const textPlaceholders =
      placeholderContext?.values ?? scenario.metadata.textPlaceholders;
    const withPlaceholders = {
      ...scenario,
      metadata: {
        ...scenario.metadata,
        textPlaceholders,
      },
    };
    const withStart = startSceneId
      ? {
          ...withPlaceholders,
          metadata: { ...withPlaceholders.metadata, startScene: startSceneId },
        }
      : withPlaceholders;
    return ScenarioLoader.resolveAssetPaths(withStart, scenarioId, { layer: assetLayer });
  }, [scenario, scenarioId, startSceneId, assetLayer, placeholderContext?.values]);

  const [overlayPortalTarget, setOverlayPortalTarget] = useState<HTMLElement | null>(null);
  const previewBodyRef = useCallback((node: HTMLDivElement | null) => {
    setOverlayPortalTarget(node);
  }, []);

  useEffect(() => {
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className={styles.previewOverlay}>
      <header className={styles.previewHeader}>
        <span>Aperçu — {previewScenario.metadata.title}</span>
        <button type="button" className={styles.btnSecondary} onClick={onClose}>
          Fermer (Échap)
        </button>
      </header>
      <div ref={previewBodyRef} className={styles.previewBody}>
        {overlayPortalTarget && (
          <GameProvider persistSaves={false}>
            <GameplayScreen
              scenario={previewScenario}
              scenarioId={scenarioId}
              onBackToMenu={onClose}
              previewMode
              overlayPortalTarget={overlayPortalTarget}
            />
          </GameProvider>
        )}
      </div>
    </div>
  );
};
